<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-70">
      <div
        class="fixed inset-0 hidden bg-black/50 md:block"
        aria-hidden="true"
        @click="close"
      />

      <div
        class="flex h-full items-stretch justify-center md:min-h-full md:items-center md:p-4"
        @click="close"
      >
        <div
          class="relative flex h-full w-full max-w-[800px] flex-col overflow-hidden bg-white md:h-auto md:max-h-[min(800px,90vh)] md:rounded-xl md:shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          @click.stop
          @keydown.escape.prevent="close"
        >
          <div class="relative flex shrink-0 items-center px-4 py-5 pr-14 md:px-6 md:py-6">
            <h2
              :id="titleId"
              class="min-w-0 text-[32px] font-medium leading-10 tracking-[-0.02em] text-neutral-900"
            >
              {{ t('contact_page.parking_modal.title') }}
            </h2>
            <button
              ref="closeButton"
              type="button"
              class="absolute top-5 right-4 z-20 mt-1 flex h-8 w-8 cursor-pointer items-center justify-center p-0 md:top-6 md:right-6"
              :aria-label="t('common.close')"
              @click="close"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M18 6 6 18M6 6l12 12"
                  stroke="#6B7280"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div class="flex min-h-0 flex-1 flex-col overflow-y-auto border-t border-neutral-200 px-4 py-5 text-left md:px-6 md:py-6">
            <div class="flex flex-wrap content-start items-start justify-start gap-2">
              <button
                v-for="parking in parkings"
                :key="parking.id"
                type="button"
                class="rounded-lg px-3 py-1.5 text-left text-sm font-medium leading-4"
                :class="parking.id === selectedId
                  ? 'bg-[#111827] text-white'
                  : 'bg-[#f3f4f6] text-[#374151] hover:bg-neutral-200'"
                :aria-pressed="parking.id === selectedId"
                @click="selectParking(parking.id)"
              >
                {{ parking.badge }}
              </button>
            </div>

            <div v-if="selected" class="mt-6 flex flex-col items-start gap-3">
              <div class="flex items-center justify-start gap-2">
                <Icon
                  name="circle-parking"
                  class="size-5 shrink-0 text-neutral-900"
                  aria-hidden="true"
                />
                <p class="text-left text-base font-semibold leading-[18px] text-neutral-900">
                  {{ selected.address }}
                </p>
              </div>
              <div class="flex items-center justify-start gap-2">
                <Icon
                  name="walk"
                  class="size-5 shrink-0 text-neutral-900"
                  aria-hidden="true"
                />
                <p class="text-left text-base leading-[18px] text-neutral-900">
                  {{ selected.walk }}
                </p>
              </div>
            </div>

            <div
              class="relative mt-6 min-h-[240px] w-full flex-1 overflow-hidden rounded-xl border border-neutral-200 transition-opacity duration-600 ease-out md:min-h-0 md:flex-none md:aspect-[16/10] lg:aspect-auto lg:h-[468px]"
              :style="{ opacity: mapLoaded ? 1 : 0.5 }"
            >
              <iframe
                :key="mapEmbedUrl"
                :src="mapEmbedUrl"
                :title="mapIframeTitle"
                class="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                allowfullscreen
                @load="mapLoaded = true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import { NEARBY_PARKINGS } from '@/utils/nearbyParkings.js';
import { useTranslations } from '@/utils/translations.js';

const props = defineProps({
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { t } = useTranslations();
const page = usePage();

const titleId = 'parking-modal-title';
const closeButton = ref(null);
const selectedId = ref(NEARBY_PARKINGS[0].id);
const mapLoaded = ref(false);

const parkings = computed(() => NEARBY_PARKINGS.map((parking) => ({
  ...parking,
  badge: t(`contact_page.parking_modal.items.${parking.id}.badge`),
  address: t(`contact_page.parking_modal.items.${parking.id}.address`),
  walk: t(`contact_page.parking_modal.items.${parking.id}.walk`),
})));

const selected = computed(() => (
  parkings.value.find((parking) => parking.id === selectedId.value) || parkings.value[0]
));

const mapEmbedUrl = computed(() => {
  const locale = page.props.current_locale === 'en' ? 'en' : 'pl';
  return `https://www.google.com/maps?q=${encodeURIComponent(selected.value.query)}&hl=${locale}&z=16&output=embed`;
});

const mapIframeTitle = computed(() => selected.value.address);

function selectParking(id) {
  if (selectedId.value === id) return;
  selectedId.value = id;
  mapLoaded.value = false;
}

function close() {
  emit('close');
}

function onEscape(event) {
  if (props.open && event.key === 'Escape') close();
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    document.body.style.setProperty('overflow', 'hidden');
    nextTick(() => closeButton.value?.focus());
  } else {
    document.body.style.removeProperty('overflow');
  }
});

onMounted(() => {
  document.addEventListener('keydown', onEscape);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onEscape);
  document.body.style.removeProperty('overflow');
});
</script>
